<template>
  <transition name="slideUp">
    <div class="notificationContainer" v-if="open">
      <div class="notificationWindow">
        <font-awesome-icon @click="closeNotifications" class="closeButton" :icon="['fas', 'xmark']" />
        <div class="notificationMessages">
          <div v-if="!hasPermissions && permissionError" class="permissionPrompt cardBackground">
            <div class="permissionContent">
              <h3>Enable Push Notifications</h3>
              <p>To receive notifications, please allow push notification permissions.</p>
              <button @click="requestPermissions" class="permissionButton">Request Permissions</button>
            </div>
          </div>
          
          <div v-if="hasPermissions" class="testPrompt cardBackground">
            <div class="testContent">
              <h3>Test Notifications</h3>
              <p>Schedule a test notification that will appear in 5 seconds, even if the app is closed.</p>
              <button @click="sendTestNotification" class="testButton">Schedule Test Notification (5s)</button>
            </div>
          </div>
          
          <div v-if="notifications.length === 0 && hasPermissions" class="noNotifications cardBackground">
            <div class="noNotificationsContent">
              <font-awesome-icon class="noNotificationIcon" :icon="['far', 'bell']" />
              <h3>No Notifications</h3>
              <p>You're all caught up!</p>
            </div>
          </div>
          
          <div v-for="(notification, i) of notifications" v-bind:key="notification.id || i" class="notificationDiv cardBackground">
            <font-awesome-icon v-if="notification.unread" @click="toggleUnread(notification)" class="bellIcon" :icon="['fas', 'bell']" />
            <font-awesome-icon v-else @click="toggleUnread(notification)" class="bellIcon" :icon="['far', 'bell']" />
            <div class="notificationContent">
              <h3>{{formatNotificationType(notification)}}</h3>
              <p>{{formatNotificationMessage(notification)}}</p>
              <small v-if="notification.timestamp" class="timestamp">
                {{new Date(notification.timestamp).toLocaleDateString()}} {{new Date(notification.timestamp).toLocaleTimeString()}}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>

import {useLoginStore} from "@/stores/loginStore";
import {useNotificationStore} from "@/stores/notificationStore";

export default {
  name: 'NotificationPage',
  components: {},
  props: {
    open: {
      type: Boolean,
      required: true,
    }
  },
  data(){
    return {
      errorTxt: '',
      currentUser: useLoginStore().getUser,
      userInput: '',
      manualUnread: false,
      notificationStore: useNotificationStore(),
    }
  },
  computed: {
    notifications() {
      return this.notificationStore.notifications;
    },
    hasPermissions() {
      return this.notificationStore.hasPermission;
    },
    permissionError() {
      return this.notificationStore.error;
    }
  },
  mounted(){

  },
  methods: {
    toggleUnread(notification){
      this.manualUnread = true
      if (notification.unread){
        this.markRead(notification)
      } else {
        this.markUnread(notification)
      }
    },
    markUnread(notification){
      this.notificationStore.markAsUnread(notification.id);
    },
    markRead(notification){
      this.notificationStore.markAsRead(notification.id);
    },
    closeNotifications(){
      if (!this.manualUnread) {
        this.notificationStore.markAllAsRead();
      }
      this.$emit('close')
    },
    async requestPermissions() {
      try {
        await this.notificationStore.requestPermissions();
      } catch (error) {
        console.error('Failed to request permissions:', error);
      }
    },
    async sendTestNotification() {
      try {
        // Import and use the push notification service directly
        const pushNotificationService = (await import('@/services/PushNotificationService')).default;
        const result = await pushNotificationService.scheduleTestNotification();
        
        if (result.success) {
          // Show immediate feedback
          this.notificationStore.addMockNotification({
            title: 'Notification Scheduled',
            body: `A test notification will appear in 5 seconds at ${result.scheduledTime.toLocaleTimeString()}`,
            type: 'System'
          });
        }
      } catch (error) {
        console.error('Failed to schedule test notification:', error);
        // Fallback to mock notification
        this.notificationStore.addMockNotification({
          title: 'Test Notification',
          body: 'This is a test notification from the app',
          type: 'System'
        });
      }
    },
    formatNotificationMessage(notification) {
      if (notification.body) {
        return notification.body;
      }
      return notification.message || 'No message';
    },
    formatNotificationType(notification) {
      return notification.type || notification.title || 'Notification';
    }
  },
}
</script>

<style scoped>
.bellIcon {
  color: var(--text-primary);
  font-size: 40px;
  cursor: pointer;
}

.noNotificationIcon {
  color: var(--text-primary);
  font-size: 48px;
  margin-bottom: 15px;
}

.permissionPrompt, .noNotifications, .testPrompt {
  width: 100%;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
}

.permissionContent, .noNotificationsContent, .testContent {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.permissionButton, .testButton {
  background: linear-gradient(180deg, #4A90E2 0%, #357ABD 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.permissionButton:hover, .testButton:hover {
  opacity: 0.9;
}

.notificationContent {
  flex: 1;
}

.timestamp {
  color: var(--text-secondary);
  font-size: 12px;
  margin-top: 8px;
  display: block;
}
h3{
  text-align: left;
}
.notificationDiv{
  width: 100%;
  padding: 15px;
  display: flex;
  flex-direction: row;
  gap: 15px;
  border-radius: 8px;
}
.notificationMessages{
  height: calc(100% - 75px);
  margin-top: 75px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 0;
  padding: 0 15px;
  gap: 15px;
}
h5{
  text-align: left;
}
.notificationWindow{
  position: relative;
}
.notificationContainer{
  width: 100vw;
  height: calc(100vh - var(--header-height));
  background-color: var(--color-background);
  position: absolute;
  top: var(--header-height);
  left: 0;
  z-index: 100;
  padding-top: 30px;
}
.notificationWindow{
  width: 100%;
  height: 100%;
  background-color: var(--color-card);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border: 1px solid var(--color-card-border);
}

/* slide-in/out animation */
.slideUp-enter-active,
.slideUp-leave-active {
  transition: transform .3s ease;
}
.slideUp-enter {
  transform: translateY(100%);
}
.slideUp-enter-to {
  transform: translateY(0);
}
.slideUp-leave {
  transform: translateY(0);
}
.slideUp-leave-to {
  transform: translateY(100%);
}
</style>
